"use client";

// ===========================================
// konakla - Çerez Tercihleri Banner (KVKK uyumlu)
// Tam genişlik, alt sabit, sağda buton bloğu
// ===========================================

import { useEffect, useId, useState } from "react";
import Link from "next/link";
import { X, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const CONSENT_STORAGE_KEY = "konakla:cookie-consent";
const CONSENT_VERSION = 1;

/**
 * Banner bir kez kapatıldıktan sonra bir daha KENDİLİĞİNDEN açılmaz. Oysa
 * /cookies ve /kvkk sayfalarında "izninizi dilediğiniz zaman geri alabilirsiniz"
 * taahhüdü dört ayrı yerde yazılı. Bu olay, tercih panelini yeniden açmak için
 * dışarıya (ör. footer'daki "Çerez Tercihleri" butonu) verilen kancadır.
 */
export const CONSENT_OPEN_EVENT = "konakla:consent-open";

/** Çerez tercihleri panelini açar. Herhangi bir istemci bileşeninden çağrılabilir. */
export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

interface ConsentState {
  version: number;
  timestamp: string;
  necessary: true; // her zaman aktif
  analytics: boolean;
  marketing: boolean;
}

function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(analytics: boolean, marketing: boolean) {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    necessary: true,
    analytics,
    marketing,
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent("konakla:consent-updated", { detail: state }));
  } catch {
    // localStorage erişilemiyorsa sessiz geç
  }
}

export function CookieBanner() {
  // `visible` zaten false başladığı için ayrı bir `mounted` guard'ına gerek yok;
  // hydration mismatch zaten olmaz çünkü hem server hem client ilk render'da
  // `null` döndürür. Effect içinde setState çağırmak React 19 kuralını ihlal
  // ediyordu — sadece consent cookie'si okunup timeout ile visible açılıyor.
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  // Kullanıcı sonradan "Çerez Tercihleri"ne basarsa paneli KAYITLI seçimlerle aç.
  useEffect(() => {
    const handleOpen = () => {
      const existing = readConsent();
      setAnalytics(existing?.analytics ?? false);
      setMarketing(existing?.marketing ?? false);
      setShowPreferences(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, handleOpen);
  }, []);

  const handleAcceptAll = () => {
    saveConsent(true, true);
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent(false, false);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent(analytics, marketing);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className={cn(
        "fixed inset-x-0 bottom-0 z-100 px-3 pb-3 sm:px-6 sm:pb-6",
        // Mobil bottom nav (h-16 + safe area) ile çakışmasın
        "pb-[calc(--spacing(20)+env(safe-area-inset-bottom))] md:pb-6"
      )}
    >
      <div className="border-border/60 mx-auto w-full max-w-7xl overflow-hidden rounded-2xl border bg-white shadow-2xl ring-1 ring-black/5">
        <div className="relative p-5 sm:p-6 md:p-7">
          {/* Geri butonu sadece tercihleri yönet ekranındayken */}
          {showPreferences && (
            <button
              type="button"
              onClick={() => setShowPreferences(false)}
              aria-label="Geri dön"
              className="text-muted-foreground hover:bg-muted hover:text-foreground absolute top-3 right-3 rounded-full p-1.5 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {!showPreferences ? (
            // ===== ANA EKRAN: Sol metin / Sağ buton bloğu =====
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:gap-8">
              {/* SOL: İçerik */}
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 hidden h-10 w-10 shrink-0 items-center justify-center rounded-full sm:flex">
                    <Cookie className="text-primary h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h2
                      id="cookie-banner-title"
                      className="text-foreground text-lg font-semibold sm:text-xl"
                    >
                      Çerez Kullanımı
                    </h2>
                    <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                      <strong className="text-foreground">konakla</strong> olarak, platformun
                      güvenli ve doğru şekilde çalışması için <strong>zorunlu çerezler</strong>{" "}
                      kullanıyoruz. Zorunlu olmayan çerezleri (ör. analitik) yalnızca izninizle
                      kullanırız. <strong>&ldquo;Tümünü Kabul Et&rdquo;</strong> seçeneği ile
                      zorunlu olmayan çerezlere de izin verebilir,{" "}
                      <strong>&ldquo;Reddet&rdquo;</strong> ile yalnızca zorunlu çerezlerle devam
                      edebilir veya <strong>&ldquo;Tercihlerimi Yönet&rdquo;</strong> ile
                      seçimlerinizi özelleştirebilirsiniz.
                    </p>
                    {/* Alt linkler */}
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <Link
                        href="/cookies"
                        className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
                      >
                        Çerez Politikası
                      </Link>
                      <span className="text-border" aria-hidden>
                        •
                      </span>
                      <Link
                        href="/kvkk"
                        className="text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
                      >
                        Aydınlatma Metni
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* SAĞ: Buton bloğu - 2 satır grid */}
              <div className="grid w-full shrink-0 grid-cols-2 gap-2 md:w-[280px]">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-primary hover:bg-primary/90 col-span-1 h-11 w-full text-white"
                >
                  Tümünü Kabul Et
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/5 col-span-1 h-11 w-full border-2 bg-white"
                >
                  Reddet
                </Button>
                <Button
                  onClick={() => setShowPreferences(true)}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/5 col-span-2 h-11 w-full border-2 bg-white"
                >
                  Tercihlerimi Yönet
                </Button>
              </div>
            </div>
          ) : (
            // ===== TERCİHLERİ YÖNET EKRANI =====
            <div className="space-y-5">
              <div className="flex items-start gap-3 pr-8">
                <div className="bg-primary/10 hidden h-10 w-10 shrink-0 items-center justify-center rounded-full sm:flex">
                  <Cookie className="text-primary h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h2 className="text-foreground text-lg font-semibold sm:text-xl">
                    Çerez Tercihleri
                  </h2>
                  <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                    Hangi çerezlere izin verdiğinizi aşağıdan seçebilirsiniz.
                  </p>
                </div>
              </div>

              <div className="space-y-2.5">
                <PreferenceRow
                  title="Zorunlu Çerezler"
                  description="Site temel işlevleri (oturum, güvenlik) için gereklidir. Devre dışı bırakılamaz."
                  checked
                  disabled
                  onChange={() => {}}
                />
                <PreferenceRow
                  title="Analitik Çerezler"
                  description="Site kullanımını anonim olarak ölçmek ve performansı iyileştirmek için kullanılır."
                  checked={analytics}
                  onChange={setAnalytics}
                />
                <PreferenceRow
                  title="Pazarlama Çerezleri"
                  description="Size daha uygun içerik ve reklam sunmak için kullanılır."
                  checked={marketing}
                  onChange={setMarketing}
                />
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/5 h-11 border-2 sm:w-auto sm:px-6"
                >
                  Tümünü Kabul Et
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  className="bg-foreground hover:bg-foreground/90 h-11 text-white sm:w-auto sm:px-6"
                >
                  Tercihleri Kaydet
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface PreferenceRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}

function PreferenceRow({ title, description, checked, disabled, onChange }: PreferenceRowProps) {
  // Anahtarın adı ve açıklaması GÖRSEL olarak kardeş elemanlarda duruyordu;
  // ekran okuyucu her üçünü de yalnızca "anahtar, kapalı" diye okuyor, KVKK
  // çerez tercihlerinde hangisini değiştirdiğini ayırt edemiyordu.
  const baslikId = useId();
  const aciklamaId = useId();
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border bg-white/50 p-3.5">
      <div className="flex-1">
        <p id={baslikId} className="text-foreground text-sm font-medium">
          {title}
        </p>
        <p id={aciklamaId} className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
          {description}
        </p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-labelledby={baslikId}
        aria-describedby={aciklamaId}
      />
    </div>
  );
}
